// Movatic WMS — Frontend | CesarFilho 932048016
// Arquivo: src/components/ScanInput.tsx
// -----------------------------------------------------------------------------
// Captura leituras de scanner "modo teclado" (Zebra DataWedge Keystroke e
// leitores USB/Bluetooth HID). Recomendado configurar no DataWedge:
//   - Prefixo: ^MVT^   (opcional, mas ajuda a diferenciar de digitação)
//   - Sufixo: Enter (CR)
// Este componente mantém foco, agrega os caracteres em rajada e dispara
// onScan(value) quando recebe Enter.
// -----------------------------------------------------------------------------

import React, { useEffect, useRef, useState } from 'react';

type Props = {
    placeholder?: string;
    onScan: (value: string) => void;
    prefix?: string;        // exemplo: '^MVT^'
    autoClearMs?: number;   // tempo pra limpar buffer se "parar de digitar" (default 800ms)
    vibrateOnScan?: boolean;
};

export default function ScanInput({
    placeholder = 'Escaneie o código…',
    onScan,
    prefix,
    autoClearMs = 800,
    vibrateOnScan = true,
}: Props) {
    const ref = useRef<HTMLInputElement>(null);
    const [buf, setBuf] = useState('');

    // Mantém foco no input para garantir recepção do teclado/scan
    useEffect(() => {
        const t = setInterval(() => ref.current?.focus(), 600);
        return () => clearInterval(t);
    }, []);

    // Limpa buffer se a rajada "esfriar"
    useEffect(() => {
        if (!buf) return;
        const t = setTimeout(() => setBuf(''), autoClearMs);
        return () => clearTimeout(t);
    }, [buf, autoClearMs]);

    const submitBuffer = () => {
        const raw = buf.trim();
        const value = prefix ? (raw.startsWith(prefix) ? raw.slice(prefix.length) : raw) : raw;
        if (value) {
            if (vibrateOnScan && 'vibrate' in navigator) {
                try { navigator.vibrate?.(15); } catch { }
            }
            onScan(value);
        }
        setBuf('');
    };

    const onKeyDown: React.KeyboardEventHandler<HTMLInputElement> = (e) => {
        if (e.key === 'Enter') {
            submitBuffer();
            e.preventDefault();
            return;
        }
        if (e.key === 'Backspace') {
            setBuf((s) => s.slice(0, -1));
            return;
        }
        if (e.key.length === 1) {
            setBuf((s) => s + e.key);
        }
    };

    const onPaste: React.ClipboardEventHandler<HTMLInputElement> = (e) => {
        const text = e.clipboardData.getData('text');
        if (text) {
            setBuf((s) => s + text);
            e.preventDefault();
        }
    };

    return (
        <input
            ref={ref}
            inputMode="none"
            autoCapitalize="off"
            autoCorrect="off"
            spellCheck={false}
            placeholder={placeholder}
            value={buf}
            onChange={() => { }}
            onKeyDown={onKeyDown}
            onPaste={onPaste}
            className="w-full px-4 py-3 rounded-xl border outline-none"
            aria-label="Campo de leitura por scanner"
        />
    );
}
